import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../i18n/translations';
import {Stack, SxProps} from '@mui/material';
import {IndexViewResults} from '../../../../../components/index-view-results/index-view-results';
import {
  EntityUserCard,
  EntityUserCardSkeleton,
} from '../../../../../components/daily-care-log/cards/entity-user';
import SearchInput from '../../../../../components/search-input/search-input';
import {PaginatedTableData} from '../../../../../interfaces/paginated-table-data';
import {AffiliatedUser} from '../../../../../backend/responses/affiliated-user';
import {CareTaskComplementary} from '../../../../../backend/interfaces/care-task-complementary';
import {ScreenPaths} from '../../../../../enums';
import {useFilteredTasks} from '../../../../../hooks/filtered-tasks';

interface UsersShiftTabViewProps {
  seniors?: Map<string, AffiliatedUser>;
  tasks?: CareTaskComplementary[];
}

const searchInputSx: SxProps = {
  padding: '13px 16px',
  height: 'auto',
  border: '1px solid var(--dcm-gray-general)',
  color: 'var(--dcm-gray-general)',
  '.MuiInputBase-input': {
    height: 'auto',
    padding: 0,
    lineHeight: '24px',
    '&::placeholder': {
      opacity: 1,
    },
  },
};

export const UsersShiftTabView = ({seniors, tasks}: UsersShiftTabViewProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [data, setData] = useState<PaginatedTableData<AffiliatedUser>>([]);
  let {entityId, entityShiftId}: {entityId: string; entityShiftId: string} = useParams();
  const {t} = useTranslation();

  // Filter the shift care tasks according to the currently active filters
  const {filteredTasks} = useFilteredTasks({tasks, seniors});

  const processSeniorCardInfo = useCallback(() => {
    setIsLoading(true);

    if (!filteredTasks) return;

    const uniqueTaskSeniorIds: string[] = [];

    const updatedSeniors: AffiliatedUser[] = [];

    const groupedSeniorTasks = new Map<string, CareTaskComplementary[]>();

    filteredTasks?.forEach((task) => {
      // index unique id's
      if (!uniqueTaskSeniorIds.includes(task.careTask.userId))
        uniqueTaskSeniorIds.push(task.careTask.userId);

      // index tasks per senior id
      if (!groupedSeniorTasks.has(task.careTask.userId))
        groupedSeniorTasks.set(task.careTask.userId, []);
      groupedSeniorTasks.get(task.careTask.userId)?.push(task);
    });

    uniqueTaskSeniorIds.forEach((id) => {
      const senior = seniors?.get(id);
      if (senior) updatedSeniors.push(senior);
    });

    const search = window.location.search;

    const gridStructured = updatedSeniors
      .filter((senior) => {
        const lowerCaseName = senior.user.name.toLowerCase();
        const lowerCaseDisplayName = senior.user.displayName?.toLowerCase();
        const lowerCaseSearchString = searchString.toLowerCase();

        return (
          lowerCaseName.includes(lowerCaseSearchString) ||
          lowerCaseDisplayName?.includes(lowerCaseSearchString)
        );
      })
      .map((senior) => {
        return {
          id: senior.user.id,
          item: senior,
          toLink: `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_SeniorTasks.replace(
            ':userId',
            senior.user.id!
          )}${search}&entityShiftId=${entityShiftId}`,
          columns: [
            {
              columnId: 'tasks',
              value: groupedSeniorTasks.get(senior?.user?.id!),
            },
          ],
        };
      });

    setData(gridStructured as PaginatedTableData<AffiliatedUser>);

    setIsLoading(false);
  }, [entityId, seniors, entityShiftId, searchString, filteredTasks]);

  useEffect(() => {
    processSeniorCardInfo();
  }, [entityId, seniors, processSeniorCardInfo, filteredTasks]);

  const handleSearchAction = (value: string) => setSearchString(value);

  const emptyStateMessage =
    searchString && searchString.length > 0
      ? {
          title: t(
            translations.daily_care_log.shift_details.tabs.user.grid.empty_search_message.title,
            {
              name: searchString,
            }
          ),
          subtitle: t(
            translations.daily_care_log.shift_details.tabs.user.grid.empty_search_message.subtitle
          ),
        }
      : {title: t(translations.daily_care_log.shift_details.tabs.user.grid.empty)};

  return (
    <Stack gap={2}>
      {((data && data?.length > 0) || searchString) && (
        <SearchInput
          placeholder={t(translations.index_grid_screen.search_placeholder)}
          onSearchAction={handleSearchAction}
          sx={searchInputSx}
        />
      )}
      <IndexViewResults
        activeViewType="grid"
        firstLoad={false}
        data={data}
        setData={setData}
        isLoading={isLoading}
        renderItem={{
          grid: {
            element: EntityUserCard,
            skeleton: EntityUserCardSkeleton,
            gridItemWidth: 146,
          },
        }}
        emptyStateMessage={emptyStateMessage}
      />
    </Stack>
  );
};
